import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { createGridStatusCell, StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback } from 'react'
import CreateLoadBalancerModal from '../CreateLoadBalancerModal'
import DeleteLoadBalancerDialog from '../DeleteLoadBalancerDialog'
import { deleteLoadBalancer, listLoadBalancers, listSubnets } from '../actions'
import { ILoadBalancerDetailsPageTabs } from '../model'
import { loadBalancersSelector } from '../selectors'
import EmptyContent from 'core/components/statusCard/EmptyContent'
import { formatTimestampToLocal } from '../../vms/helpers'

type ModelDataKey = DataKeys.LoadBalancers
type SelectorModel = ArrayElement<ReturnType<typeof loadBalancersSelector>>
type ActionParams = InferActionParams<typeof listLoadBalancers>
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const Phase = {
  Pending: 'Pending',
  Active: 'Active',
  Error: 'Error',
  Deleting: 'Deleting',
  Unknown: 'Unknown',
}

const getPhaseStatus = (phase): StatusCellModel => {
  const normalizedPhase = phase.trim().toLowerCase()
  switch (normalizedPhase) {
    case Phase.Active.toLowerCase():
      return { variant: 'success', label: 'Active' }
    case Phase.Pending.toLowerCase():
      return { variant: 'warning', label: 'Pending' }
    case Phase.Error.toLowerCase():
      return { variant: 'error', label: 'Error' }
    case Phase.Deleting.toLowerCase():
      return { variant: 'error', label: 'Deleting' }
    default:
      return { variant: 'unknown', label: Phase.Unknown }
  }
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'LoadBalancers',
  listTablePrefs,
)

const searchTargets = ['name', 'ipAddress', 'operatingStatus', 'provisioningStatus', 'adminStateUp']

const searchFunctions = [
  {
    property: 'subnetDetails',
    searchFn: (value, searchString) => {
      return value.some(
        (subnet) =>
          subnet.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          subnet.cidr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
      )
    },
  },
]

const loadBalancerColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    // width: 'medium',
  },
  {
    key: 'provisioningStatus',
    label: 'Status',
    CellComponent: createGridStatusCell({
      dataFn: (phase): StatusCellModel => {
        return getPhaseStatus(phase)
      },
    }),
  },
  {
    key: 'ipAddress',
    label: 'VIP Address',
  },

  {
    key: 'provider',
    label: 'Provider',
  },
  {
    key: 'subnetName',
    label: 'Subnet',
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

export default function LoadBalancersListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { prefs } = useScopedPreferences()

  const { message, loading, reload } = useListAction(listLoadBalancers)
  const { reload: reloadSubnets } = useListAction(listSubnets)
  const data = useAppSelector(loadBalancersSelector)

  const handleRefresh = useCallback(() => {
    reload(true)
    reloadSubnets(true)
  }, [reload, reloadSubnets])
  return (
    <>
      <CreateLoadBalancerModal addRoute={routeManager.routes.openstack.createLoadBalancers} />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.LoadBalancers}
        label="Load Balancers"
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={'Loading...'}
        onRefresh={handleRefresh}
        data={data}
        columns={loadBalancerColumns}
        addUrl={routeManager.routes.openstack.createLoadBalancers.path({
          tab: ILoadBalancerDetailsPageTabs.LoadBalancers,
        })}
        addText="Create Load Balancer"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteLoadBalancer}
        DeleteDialogComponent={DeleteLoadBalancerDialog}
        multiSelection
        emptyContent={
          <EmptyContent
            title={'No load balancers'}
            subTitle={'Create one in order to see details'}
          />
        }
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
